import { Button, Stack, Typography, darken } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import MultipleNumberInput from 'src/components/common/MultipleNumberInput/MultipleNumberInput';
import { useLocale } from '@repo/i18n';
import { tzdate, TZDate } from '@repo/tzdate';
import { DateRange } from '@mui/x-date-pickers-pro';
import { AccommodationResultList } from '../components/AccommodationResultList';
import { Availabilities } from '../components/Availabilities';
import { useCustomizations } from 'src/components/utils/theme/customizations';
import { BREAKPOINT_MD } from '../utils';
import { useAvailabilities } from '../hooks/availabilities';
import {
    useAccommodationProducts,
    useProducts,
    useNextProductAvailabilityByProductIds,
} from '@repo/widget-utils/services/hooks/product';
import { capitalize } from '@repo/common-utils/TextUtils';
import { useConfigurations } from '@repo/widget-utils/widgetsConfiguration';
import { minBy } from 'lodash-es';
import { useAccommodationSearchQuantities } from 'src/hooks/domain/booking/useAccommodationSearchQuantities';

type Props = {
    ids: string[];
    provider: 'visbook' | 'bilberry';
};

export function AccommodationSearchForm(props: Props) {
    const { ids, provider = 'visbook' } = props;
    const customizations = useCustomizations();
    const { t } = useLocale();
    const config = useConfigurations();
    // Use accommodation search quantities hook with personsMax constraint
    const { roomCountItems, guestItemsPerRoom, onChangeRoomCount, onChangeGuestCount, guests } =
        useAccommodationSearchQuantities(config.personsMax);

    const [selectedDateRange, setSelectedDateRange] = useState<DateRange<TZDate>>([null, null]);
    const [showAvailability, setShowAvailability] = useState(false);

    const [searchDateRange, setSearchDateRange] = useState<DateRange<TZDate>>([
        TZDate.now(),
        TZDate.now().endOf('month').add(1, 'day').startOf('day'),
    ]);

    const { data: bilberryProviderProducts } = useProducts({
        ids: provider === 'bilberry' ? ids : [],
    });

    const { data: nextBilberryProductAvailabilities } = useNextProductAvailabilityByProductIds(
        bilberryProviderProducts.map((p) => p.id),
    );

    const { data: availabilities, isLoading: availabilitiesIsLoading } = useAvailabilities(
        provider,
        ids,
        searchDateRange,
        bilberryProviderProducts,
    );

    const earliestNextBilberryProviderProductAvailability = useMemo(() => {
        if (!nextBilberryProductAvailabilities || nextBilberryProductAvailabilities.length === 0)
            return undefined;
        const minNext = minBy(nextBilberryProductAvailabilities, (x) => x.next);
        const minNextProductDate = tzdate(minNext?.next, false);
        return minNextProductDate;
    }, [nextBilberryProductAvailabilities]);

    useEffect(() => {
        if (!earliestNextBilberryProviderProductAvailability) return;

        const searchFrom = earliestNextBilberryProviderProductAvailability.startOf('month');
        const searchTo = searchFrom.endOf('month').add(1, 'day').startOf('day');

        setSearchDateRange([searchFrom, searchTo]);
    }, [earliestNextBilberryProviderProductAvailability]);

    const {
        data: possibleAccommodations,
        isLoading: isLoadingPossibleAccommodations,
        error: isErrorPossibleAccommodations,
    } = useAccommodationProducts(
        ids,
        selectedDateRange,
        guests,
        provider === 'visbook' && selectedDateRange[0] !== null && selectedDateRange[1] !== null,
        true,
    );

    return (
        <Stack
            gap={3}
            p={2}
            pt={3}
            borderRadius={customizations.borderRadius + 'px'}
            bgcolor={customizations.accommodationSearchFormColor}
            color={customizations.accommodationSearchFormColorContrast}
            sx={{
                containerType: 'inline-size',
                containerName: 'accommodationsearchform',
            }}
        >
            <Stack
                gap={2}
                sx={{
                    [`@container (width > ${BREAKPOINT_MD})`]: {
                        flexDirection: 'row',
                    },
                }}
            >
                <Stack
                    gap={2}
                    width="100%"
                    sx={{
                        [`@container (width > ${BREAKPOINT_MD})`]: {
                            maxWidth: '50%',
                        },
                    }}
                >
                    <Typography
                        variant="h3"
                        m={0}
                        color={customizations.accommodationSearchFormColorContrast}
                    >
                        {t.searchAndBookAccommodations}
                    </Typography>
                    <Stack>
                        <MultipleNumberInput
                            onChange={(id, value) => {
                                onChangeRoomCount(id, value);
                                setSelectedDateRange([null, null]);
                            }}
                            items={roomCountItems}
                            variant="filled"
                            labelText={capitalize(t.number_of_rooms)}
                            labelColor={customizations.accommodationSearchFormColorContrast}
                            backgroundColor={customizations.accommodationSearchFormInputColor}
                            color={customizations.accommodationSearchFormInputTextColor}
                        />
                    </Stack>
                    {guestItemsPerRoom.map((items, roomIdx) => (
                        <Stack key={roomIdx} gap={0.5}>
                            <Typography
                                variant="h4"
                                m={0}
                                fontSize={16}
                                fontWeight="bold"
                                color={customizations.accommodationSearchFormColorContrast}
                            >
                                {capitalize(t.rooms.num(roomIdx + 1)) + ' ' + (roomIdx + 1)}
                            </Typography>
                            <MultipleNumberInput
                                onChange={(id, value) => {
                                    onChangeGuestCount(roomIdx, id, value);
                                    setSelectedDateRange([null, null]);
                                }}
                                items={items}
                                variant="filled"
                                labelText={t.selectNumberOfGuests}
                                labelColor={customizations.accommodationSearchFormColorContrast}
                                backgroundColor={customizations.accommodationSearchFormInputColor}
                                color={customizations.accommodationSearchFormInputTextColor}
                            />
                        </Stack>
                    ))}
                    {!showAvailability && (
                        <Button
                            variant="outlined"
                            color="primary"
                            sx={{
                                borderColor: customizations.accommodationSearchFormColorContrast,
                                backgroundColor: customizations.accommodationSearchFormColor,
                                color: customizations.accommodationSearchFormColorContrast,
                                '&:hover': {
                                    backgroundColor: darken(
                                        customizations.accommodationSearchFormColor,
                                        0.2,
                                    ),
                                    color: customizations.accommodationSearchFormColorContrast,
                                },
                            }}
                            size="large"
                            onClick={() => {
                                setShowAvailability(true);
                            }}
                        >
                            {showAvailability ? t.updateAvailability : t.checkAvailability}
                        </Button>
                    )}
                </Stack>
                {showAvailability && (
                    <Availabilities
                        availabilities={availabilities}
                        availabilitiesIsLoading={availabilitiesIsLoading}
                        provider={provider}
                        selectedDateRange={selectedDateRange}
                        setSelectedDateRange={setSelectedDateRange}
                        guests={guests}
                        setSearchDateRange={setSearchDateRange}
                        minSearchDate={earliestNextBilberryProviderProductAvailability}
                    />
                )}
            </Stack>

            <Stack gap={3}>
                {showAvailability &&
                    selectedDateRange[0] &&
                    selectedDateRange[1] &&
                    new Array(roomCountItems[0].value).fill(null).map((_, roomIdx) => {
                        return (
                            <Stack key={roomIdx} gap={1}>
                                <Typography
                                    variant="h4"
                                    m={0}
                                    fontSize={16}
                                    fontWeight="bold"
                                    color={customizations.accommodationSearchFormColorContrast}
                                >
                                    {capitalize(t.rooms.num(roomIdx + 1)) + ' ' + (roomIdx + 1)}
                                </Typography>
                                <AccommodationResultList
                                    availabilities={availabilities}
                                    selectedDateRange={selectedDateRange}
                                    guests={guests}
                                    provider={provider}
                                    roomIdx={roomIdx}
                                    possibleAccommodations={possibleAccommodations}
                                    isErrorPossibleAccommodations={isErrorPossibleAccommodations}
                                    isLoadingPossibleAccommodations={
                                        isLoadingPossibleAccommodations
                                    }
                                />
                            </Stack>
                        );
                    })}
            </Stack>
        </Stack>
    );
}
